(function () {
  'use strict';

  const ROOT_SELECTOR = '[data-photo-handoff]';

  // assets/bot-trap.js (task TL4), loaded by this page's section before this file.
  const BotTrap = window.BotTrap;
  const STATE_SELECTOR = '[data-photo-handoff-state]';
  const CAPTURE_BUTTON_SELECTOR = '[data-photo-handoff-capture-button]';
  const FILE_INPUT_SELECTOR = '[data-photo-handoff-file-input]';
  // TASK TL18 item 3. A second, capture-less input/button pair — see sections/photo-handoff.liquid
  // for why `capture="user"` on the input above hides the gallery on several mobile browsers.
  const LIBRARY_BUTTON_SELECTOR = '[data-photo-handoff-library-button]';
  const LIBRARY_INPUT_SELECTOR = '[data-photo-handoff-library-input]';
  const RETRY_BUTTON_SELECTOR = '[data-photo-handoff-retry-button]';

  // Fixed pattern, matches product-photo-upload-modal.js's own polling target exactly —
  // this is the one handoff endpoint the contract shows as a stable pattern rather than a
  // server-provided value (unlike the photo-upload target — see uploadPhoto() below).
  const HANDOFF_STATUS_URL_PREFIX = '/apps/personalization/handoff/';

  // Mirrors ALLOWED_FILE_TYPES in assets/product-photo-upload-modal.js exactly — this page
  // feeds the same eventual pipeline (applyCapturedFile() on the desktop side, once the
  // desktop polls this photo back), so a file this page accepts and one that pipeline
  // accepts must never disagree.
  const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  class PhotoHandoffPage {
    constructor(root) {
      this.root = root;
      this.states = {};

      root.querySelectorAll(STATE_SELECTOR).forEach((el) => {
        this.states[el.dataset.photoHandoffState] = el;
      });

      this.captureButton = root.querySelector(CAPTURE_BUTTON_SELECTOR);
      this.fileInput = root.querySelector(FILE_INPUT_SELECTOR);
      this.libraryButton = root.querySelector(LIBRARY_BUTTON_SELECTOR);
      this.libraryInput = root.querySelector(LIBRARY_INPUT_SELECTOR);
      this.retryButtons = Array.from(root.querySelectorAll(RETRY_BUTTON_SELECTOR));

      const params = new URLSearchParams(window.location.search);
      this.token = params.get('token');

      /**
       * The upload path is DERIVED from the token, never read from this page's own query
       * string.
       *
       * MEASURED 2026-09-24 on the live store: the server returns
       * `apps/personalization/handoff/<token>/photo` WITHOUT a leading slash
       * (`personalization-handoff.service.ts:126` and `:155`), the validator that used to
       * stand here required one, so the value was discarded, no request was ever made, and
       * the customer saw "We couldn't send your photo" while the server log contained no
       * POST at all.
       *
       * Loosening that validator would have been worse than the bug: a path without a
       * leading slash resolves against THIS page's address, so `/pages/photo-handoff` plus
       * `apps/...` POSTs to `/pages/apps/personalization/...` — a storefront 404 instead of
       * a visible refusal.
       *
       * Deriving it removes the reason the validator existed at all. `uploadPath` used to
       * arrive through a query string anyone can hand-craft, so a tampered or reprinted QR
       * could aim a parent's photo of their own child at an attacker's origin while this
       * page still looked and behaved like the real store — the URL bar being the only
       * difference, and almost nobody checks it mid-task on a phone. A path built here can
       * only ever address this store's own handoff endpoint. The token is encoded exactly
       * as the status call already encodes it, so it cannot escape its path segment either.
       */
      this.uploadPath = this.token
        ? HANDOFF_STATUS_URL_PREFIX + encodeURIComponent(this.token) + '/photo'
        : null;

      // What "Try again" / "Take another photo" actually does depends on how the flow
      // arrived at whichever state is showing it — re-check the token's status, or just go
      // back to letting the customer pick a photo. Set explicitly at every transition that
      // shows a retry button (see the state table in this task's run report), never
      // inferred from which state it is, since "network-error" is reachable from both the
      // initial status check AND a failed upload, each needing a different recovery.
      this.retryAction = 'recheck';

      this.handleCaptureClick = this.handleCaptureClick.bind(this);
      this.handleLibraryClick = this.handleLibraryClick.bind(this);
      this.handleFileChange = this.handleFileChange.bind(this);
      this.handleRetryClick = this.handleRetryClick.bind(this);

      this.init();
    }

    init() {
      if (this.captureButton && this.fileInput) {
        this.captureButton.addEventListener('click', this.handleCaptureClick);
        this.fileInput.addEventListener('change', this.handleFileChange);
      }

      if (this.libraryButton && this.libraryInput) {
        this.libraryButton.addEventListener('click', this.handleLibraryClick);
        this.libraryInput.addEventListener('change', this.handleFileChange);
      }

      this.retryButtons.forEach((button) => {
        button.addEventListener('click', this.handleRetryClick);
      });

      // A token missing from the URL entirely is not something a retry can fix — the QR/
      // link itself was malformed or stripped, and there is nothing on this page to recover
      // with. Terminal state, no retry button in the markup for it.
      if (!this.token) {
        this.setState('no-token');
        return;
      }

      this.checkStatus();
    }

    setState(name) {
      Object.keys(this.states).forEach((key) => {
        const el = this.states[key];

        if (el) {
          el.hidden = key !== name;
        }
      });

      // TASK TL4: the clock starts when the page is actually ready to take a photo, not at load
      // — the token check runs first and can take a moment.
      if (name === 'ready') {
        BotTrap.markOpened(this.root, Date.now());
      }
    }

    /**
     * A token in the URL is not proof it is still alive — this is the first thing the page
     * does, before ever showing the capture button, so an expired code says so immediately
     * rather than after the customer has already taken a photo and tried to send it.
     */
    async checkStatus() {
      this.setState('checking');
      this.retryAction = 'recheck';

      let payload;

      try {
        payload = await this.getJson(HANDOFF_STATUS_URL_PREFIX + encodeURIComponent(this.token));
      } catch (error) {
        console.warn('[photo-handoff] Status check failed', {
          message: error?.message || null,
          status: typeof error?.status === 'number' ? error.status : null
        });
        this.setState('network-error');
        return;
      }

      if (payload && payload.alive === false) {
        this.setState('expired');
        return;
      }

      this.setState('ready');
    }

    handleCaptureClick(event) {
      event.preventDefault();

      // A real click on a real, visible button — same reasoning as
      // handleCameraFallbackTriggerClick() in product-photo-upload-modal.js: this is the
      // gesture, run synchronously, that makes .click() on the hidden file input reliable
      // across target browsers.
      this.fileInput.click();
    }

    handleLibraryClick(event) {
      event.preventDefault();

      // Same reasoning as handleCaptureClick() above — a real click on a real button is
      // what makes the following .click() on the hidden input reliable.
      this.libraryInput.click();
    }

    // Shared 'change' handler for both inputs — `event.target` is whichever one fired, so
    // this reads and clears that input specifically rather than assuming `this.fileInput`.
    handleFileChange(event) {
      const input = event.target;
      const file = input.files && input.files[0];
      input.value = '';

      if (!file) {
        return;
      }

      // Checked here, before any network request — the type is already knowable from the
      // file alone, so there is no reason to make a round trip just to fail this check on
      // the server instead. handleUploadFailure() below still recognizes a server-side
      // rejection of this same kind, as a backstop, not the primary path.
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        this.retryAction = 'ready';
        this.setState('wrong-type');
        return;
      }

      this.uploadPhoto(file);
    }

    /**
     * POSTs the raw bytes to `this.uploadPath` — DERIVED from the token in the constructor,
     * never read from this page's URL. The docblock on that assignment carries the full
     * reasoning; this one exists only so nobody re-reads this method and concludes the path
     * still arrives from outside. Mirrors uploadPhotoToService() in
     * product-photo-upload-modal.js exactly: raw bytes as the body, Content-Type set to the
     * file's own type, no wrapper.
     */
    async uploadPhoto(file) {
      // TASK TL4, reworked 2026-10-13. Only the TRAP can stop an upload here — no `enforceTime`,
      // so a fast upload is sent with its elapsed time attached rather than discarded. On a trap
      // trip nothing is uploaded and the page shows the same success state a real upload
      // produces, so a bot learns nothing from the difference.
      const trap = BotTrap.evaluate(this.root, Date.now());

      if (trap.ignore) {
        this.setState('success');
        return;
      }

      this.setState('uploading');

      if (!this.uploadPath) {
        console.warn('[photo-handoff] No token on this page — cannot build an upload path');
        this.retryAction = 'ready';
        this.setState('upload-failed');
        return;
      }

      let response;

      try {
        response = await fetch(BotTrap.appendSignals(this.uploadPath, trap), {
          method: 'POST',
          headers: { 'Content-Type': file.type },
          body: file
        });
      } catch (error) {
        console.warn('[photo-handoff] Upload request failed to send', { message: error?.message || null });
        this.retryAction = 'ready';
        this.setState('network-error');
        return;
      }

      if (response.ok) {
        this.retryAction = 'ready';
        this.setState('success');
        return;
      }

      this.handleUploadFailure(response);
    }

    /**
     * Exact server error codes are not yet known — this endpoint does not exist in
     * production yet (see this task's server dependency, `the-token-carries-the-photo-
     * before-the-book-exists`). Matched here by the most plausible code name AND by HTTP
     * status as a fallback signal, so a server answering with a status this page recognizes
     * still lands on the right message even if the exact code string differs once the real
     * server ships — see this task's run report for what to reconcile.
     */
    async handleUploadFailure(response) {
      let payload = null;

      try {
        payload = await response.json();
      } catch {
        payload = null;
      }

      const code = payload && payload.error ? payload.error.code : null;

      this.retryAction = 'ready';

      if (response.status === 410 || code === 'HANDOFF_EXPIRED' || code === 'TOKEN_EXPIRED') {
        this.setState('expired');
        return;
      }

      if (response.status === 409 || code === 'HANDOFF_PHOTO_LIMIT' || code === 'PHOTO_LIMIT_REACHED') {
        this.setState('limit');
        return;
      }

      if (response.status === 415 || code === 'VALIDATION_ERROR' || code === 'UNSUPPORTED_MEDIA_TYPE') {
        this.setState('wrong-type');
        return;
      }

      // Not one of the four named failure reasons above — still names the actual action
      // that failed (sending the photo), not a blanket "something went wrong".
      this.setState('upload-failed');
    }

    handleRetryClick(event) {
      event.preventDefault();

      if (this.retryAction === 'recheck') {
        this.checkStatus();
      } else {
        this.setState('ready');
      }
    }

    async getJson(url) {
      const response = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' }
      });

      let payload = null;

      try {
        payload = await response.json();
      } catch {
        payload = null;
      }

      if (!response.ok) {
        const error = new Error('Request failed');
        error.status = response.status;
        throw error;
      }

      return payload;
    }
  }

  function init() {
    const root = document.querySelector(ROOT_SELECTOR);

    if (!root) {
      return;
    }

    new PhotoHandoffPage(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
